import React, { useEffect, useRef, useState } from 'react';
import { Button, FormCheckbox } from '@praktio/common-ui';
import { Col, Row } from 'react-bootstrap';
import { cloneDeep, isEqual } from 'lodash';
import { useDispatch } from 'react-redux';

import { RightPaneHeader } from '../../common/RightPane/RightPane';
import ReportNotification from '../../common/ReportNotification';
import TeamManagers from '../../common/TeamManagers';
import { NotificationFrequency } from '../../../utils/constants';
import { bulkUpdateNotification } from '../../../ducks/teams';

interface IBulkReportNotificationsProps {
  teamId: string;
  teamManagers: IUser[];
  closeButtonAction: () => void;
}

const BulkReportNotifications = ({
  teamId,
  teamManagers,
  closeButtonAction,
}: IBulkReportNotificationsProps) => {
  const dispatch = useDispatch();
  const [savedChanges, setSavedChanges] = useState(false);
  const [selectedRows, setSelectedRows] = useState<IUser[]>([]);
  const [accept, setAccept] = useState(false);
  const [notification, setNotification] = useState<IReportNotification>({
    frequency: NotificationFrequency.Never,
  });
  const [disableSave, setDisableSave] = useState(false);

  const notificationRef = useRef<IReportNotification>(notification);
  const disableSaveRef = useRef<boolean>(disableSave);

  const updatingNeverFrequency =
    !!selectedRows.find(
      (manager) => manager.reportNotification?.frequency === NotificationFrequency.Never,
    ) && notification.frequency !== NotificationFrequency.Never;

  useEffect(() => {
    const newNotification = cloneDeep(notification);
    if (
      newNotification.frequency === NotificationFrequency.Weekly &&
      newNotification.day === undefined
    ) {
      newNotification.day = 0;
    }
    if (
      newNotification.frequency === NotificationFrequency.Monthly &&
      newNotification.date === undefined
    ) {
      newNotification.date = 1;
    }
    if (!isEqual(newNotification, notification)) {
      setNotification(newNotification);
    }
    notificationRef.current = notification;
  }, [notification]);

  useEffect(() => {
    if (disableSaveRef.current === true && disableSave === false) {
      setSavedChanges(false);
    }
  }, [disableSave]);

  useEffect(() => {
    if (
      selectedRows.length === 0 ||
      !notification.frequency ||
      (notification.frequency === NotificationFrequency.Weekly &&
        (notification.day === undefined || NotificationFrequency.day < 0)) ||
      (notification.frequency === NotificationFrequency.Monthly && !notification.date) ||
      (updatingNeverFrequency && !accept)
    ) {
      setDisableSave(true);
    } else {
      setDisableSave(false);
    }

    disableSaveRef.current = disableSave;
  }, [accept, disableSave, notification, selectedRows, updatingNeverFrequency]);

  const handleSubmit = async (event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    if (selectedRows.length !== 0) {
      const updatedManagers = cloneDeep(selectedRows).map((updatedManager) => {
        if (notification.frequency) {
          updatedManager.reportNotification = notification;
        }

        return updatedManager;
      });

      try {
        await dispatch(bulkUpdateNotification(teamId, updatedManagers));
        setSavedChanges(true);
        setSelectedRows([]);
        setNotification({ frequency: NotificationFrequency.Never });
      } catch (e) {
        console.error('Unable to update notifications:', e);
      }
    }
  };

  const onRowSelect = ({ selected, all }: { selected?: IUser; all?: boolean }) => {
    if (all) {
      if (selectedRows.length === teamManagers.length) {
        setSelectedRows([]);
      } else {
        setSelectedRows([...teamManagers]);
      }
      return;
    }
    if (selected) {
      const newRows = cloneDeep(selectedRows);
      const indexOfUserInSelected = newRows.findIndex((row) => row._id === selected._id);
      // Not yet in selectedRows - add
      if (indexOfUserInSelected === -1) {
        newRows.push(selected);
        // already in selectedRows - remove
      } else {
        newRows.splice(indexOfUserInSelected, 1);
      }
      setSelectedRows(newRows);
    }
  };

  return (
    <div className="BulkReportNotifications">
      <RightPaneHeader
        headerText={'Bulk Set Report Notification'}
        savedChanges={savedChanges}
        unsavedChanges={!disableSave}
        closeButtonAction={closeButtonAction}
        mainButton={
          <Button
            disabled={disableSave}
            onClick={async (event) => {
              await handleSubmit(event);
            }}
            form="admin-user-form"
            btnStyle="primary"
          >
            SAVE
          </Button>
        }
      />
      <Row>
        <Col>
          <ReportNotification
            notification={notification}
            onChange={(value, prop) => {
              if (prop) {
                setNotification({ ...notification, [prop]: value });
              }
            }}
          />
          <TeamManagers
            teamManagers={teamManagers}
            onRowSelect={onRowSelect}
            selectedRows={selectedRows}
          />
          {updatingNeverFrequency && (
            <FormCheckbox
              id="never-warning"
              label="WARNING: Some selected users have a Report Notification set to Never. Updating this will
        enable notifications for those users. Check to continue"
              checked={accept}
              onChange={() => setAccept(!accept)}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default BulkReportNotifications;
